/**
 * One Chargers page (Warren, 28 October 2026: "'Chargers' and 'Network Chargers': tell me what each shows. If they
 * overlap, make one Chargers page with a scope filter (EVI's own, whole network) and keep the old route redirecting.").
 *
 * They overlapped completely for the Super Admin: GET /chargers is cross-tenant for them, so both pages listed every
 * active charger. So /chargers is the one page. The Super Admin, and only the Super Admin, chooses a scope, carried in
 * ?scope=network (absent means EVI's own). Both of their scopes read GET /admin/chargers/oversight, which 403s anyone
 * else and is the only list that can narrow to one operator: EVI's own adds tenant_id (the signed-in Super Admin's own
 * tenant, the EVI operator), the whole network sends none. Every other role keeps exactly today's GET /chargers.
 * Pure, apart from the guarded sessionStorage that Back to Chargers reads: ChargersPage, the charger pages and App.tsx
 * only wire these to the page and the router.
 */
export type ChargerScope = 'own' | 'network';

/** The old Network Chargers address, which now opens the whole network. */
export const CHARGER_OVERSIGHT_PATH = '/chargers/oversight';
/** The EVI shortcuts' Network Chargers row (config/nav.ts). */
export const NETWORK_CHARGERS_HREF = '/chargers?scope=network';

type ScopeUser = { role?: string | null; tenant_id?: string | null } | null | undefined;

/** Only the Super Admin chooses: the oversight endpoint answers no one else (charger-oversight.ts). */
export function canChooseScope(user: ScopeUser): boolean {
  return user?.role === 'super_admin';
}

/** The scope in this URL. Anyone who cannot choose is always on their own list, so a pasted link never 403s. */
export function readScope(search: string, user: ScopeUser): ChargerScope {
  return canChooseScope(user) && new URLSearchParams(search).get('scope') === 'network' ? 'network' : 'own';
}

/** The query string for a scope, every other parameter kept: ?scope=network for the whole network, none for EVI's own. */
export function scopeSearch(search: string, scope: ChargerScope): string {
  const params = new URLSearchParams(search);
  params.delete('scope');
  if (scope === 'network') params.set('scope', 'network');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export interface ChargerListFilters {
  page: number;
  search: string;
  /** Today's All / Online / Offline, for everyone who cannot choose a scope. */
  isOnline?: boolean;
  offlineOnly: boolean;
  residentialOnly: boolean;
}

export type ChargerListRequest =
  | { api: 'chargers'; params: { page: number; limit: number; search?: string; is_online?: boolean } }
  | { api: 'oversight'; params: { page: number; limit: number; q?: string; offline_only?: boolean; residential_only?: boolean; tenant_id?: string } };

/** Which list the page reads, with what. */
export function chargerListRequest(user: ScopeUser, scope: ChargerScope, f: ChargerListFilters): ChargerListRequest {
  if (!canChooseScope(user)) {
    return { api: 'chargers', params: { page: f.page, limit: 20, search: f.search || undefined, is_online: f.isOnline } };
  }
  return {
    api: 'oversight',
    params: {
      page: f.page, limit: 25, q: f.search || undefined,
      offline_only: f.offlineOnly || undefined, residential_only: f.residentialOnly || undefined,
      ...(scope === 'own' ? { tenant_id: user?.tenant_id ?? undefined } : {}),
    },
  };
}

/** Where /chargers/oversight goes: the one Chargers page on the whole network, every other parameter kept. */
export function oversightRedirectUrl(search: string): string {
  return `/chargers${scopeSearch(search, 'network')}`;
}

/**
 * A move that changes only the query (the EVI shortcuts' Network Chargers, /chargers?scope=network, opened from
 * /chargers) renders no new route, so App.tsx announces it with this event. The Chargers page (its ?scope=) and the
 * Sessions page (its ?tab=active, lib/sessionsViews.ts) listen for it. A synthetic popstate would make every page that
 * follows Back (the site picker, Money's tabs, the reports) re-read its URL on a plain menu click.
 */
export const QUERY_CHANGE_EVENT = 'evi:querychange';

type UrlParts = { pathname: string; search: string };

/** Whether a navigation changed the query and nothing else. */
export function isQueryOnlyMove(from: UrlParts, to: UrlParts): boolean {
  return from.pathname === to.pathname && from.search !== to.search;
}

/**
 * Only the newest list request may fill the page. A late reply from an earlier one (another scope, page, search or
 * filter) is dropped, so the rows shown, and the bulk actions on them, are always the ones last asked for.
 */
export function latestRequestGate(): { begin: () => number; isLatest: (ticket: number) => boolean } {
  let latest = 0;
  return { begin: () => ++latest, isLatest: (ticket) => ticket === latest };
}

/** The Vendor / Model cell of an oversight row: what is known of the two, else Unknown. */
export function vendorModelLabel(row: { vendor: string | null | undefined; model: string | null | undefined }): string {
  return [row.vendor, row.model].map((v) => v?.trim()).filter(Boolean).join(' ') || 'Unknown';
}

/**
 * Back to Chargers returns to the Chargers list this viewer was last on in this tab, its scope kept, else /chargers.
 * A convenience for this viewer only, so this tab's sessionStorage: blocked storage (a private window) just means
 * /chargers, and only a Chargers list address is ever stored or followed.
 */
export const LAST_CHARGERS_LIST_KEY = 'evi.admin.chargers.lastList';
const CHARGERS_LIST_URL = /^\/chargers(\?[^#]*)?$/;

export function isChargersListUrl(url: string | null | undefined): url is string {
  return typeof url === 'string' && CHARGERS_LIST_URL.test(url);
}

export function rememberChargersList(url: string, storage: Pick<Storage, 'setItem'> | null = safeSessionStorage()): void {
  if (!isChargersListUrl(url)) return;
  try { storage?.setItem(LAST_CHARGERS_LIST_KEY, url); } catch { /* not remembered this time */ }
}

export function chargersBackHref(storage: Pick<Storage, 'getItem'> | null = safeSessionStorage()): string {
  try {
    const url = storage?.getItem(LAST_CHARGERS_LIST_KEY);
    return isChargersListUrl(url) ? url : '/chargers';
  } catch { return '/chargers'; }
}

function safeSessionStorage(): Storage | null {
  try { return typeof window === 'undefined' ? null : window.sessionStorage; } catch { return null; }
}
